'use client'

import * as React from 'react'
import * as Sentry from '@sentry/react'
import { PaymentsDialogProvider } from '../../components/payments-dialog-wrapper-context'
import { PAYMENT_ENTRY_POINTS } from '../../payments-dialog/analytics'
import { SentryFilterTagOneClickUpgradeButton } from '@repo/sentry-utils/constants'
import type { UpgradeSource } from '../../upgrade-sources'
import { useActivateTrialController } from '../hooks/use-activate-trial-controller'

type SubscriptionVariant = 'all-access' | 'studio-ai'

type ActivateTrialContextValue = ReturnType<typeof useActivateTrialController> & {
  onSettled?: () => void
  subscriptionType: SubscriptionVariant
}

const ActivateTrialContext = React.createContext<ActivateTrialContextValue | null>(null)

export interface ActivateTrialRootProps {
  children: React.ReactNode
  entryPoint?: PAYMENT_ENTRY_POINTS
  noReload?: boolean
  onSettled?: () => void
  sentryComponentTag?: string
  subscriptionType?: SubscriptionVariant
  upgradeSource: UpgradeSource
  shouldApplyBonus?: boolean
}

export function ActivateTrialRoot({
  children,
  entryPoint = PAYMENT_ENTRY_POINTS.ONE_CLICK_UPGRADE_BUTTON,
  noReload = false,
  onSettled,
  sentryComponentTag = SentryFilterTagOneClickUpgradeButton,
  subscriptionType = 'all-access',
  upgradeSource,
  shouldApplyBonus = false,
}: ActivateTrialRootProps) {
  const controller = useActivateTrialController({
    entryPoint,
    noReload,
    onSettled,
    subscriptionType,
    upgradeSource,
    shouldApplyBonus,
  })

  const value = React.useMemo(
    () => ({
      ...controller,
      onSettled,
      subscriptionType,
    }),
    [controller, onSettled, subscriptionType],
  )

  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        scope.setTag('component', sentryComponentTag)
      }}
    >
      <PaymentsDialogProvider useCase="one-click-upgrade">
        <ActivateTrialContext.Provider value={value}>{children}</ActivateTrialContext.Provider>
      </PaymentsDialogProvider>
    </Sentry.ErrorBoundary>
  )
}

export function useActivateTrialRootContext() {
  const context = React.useContext(ActivateTrialContext)

  if (!context) {
    throw new Error('ActivateTrial compound components must be used within ActivateTrial.Root')
  }

  return context
}
